import React from 'react';
import { Mic, MicOff } from 'lucide-react';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';

export interface VoiceDictationButtonProps {
  onTranscript: (text: string) => void;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  title?: string;
  disabled?: boolean;
  /**
   * If true, renders a button with a visible text label beside the mic icon
   */
  showLabel?: boolean;
  label?: string;
}

const SIZE_STYLES = {
  xs: {
    btn: 'h-6 w-6 text-xs',
    icon: 12,
    badge: 'text-[10px] py-0.5 px-1.5',
  },
  sm: {
    btn: 'h-7 w-7 text-xs',
    icon: 14,
    badge: 'text-[10px] py-0.5 px-2',
  },
  md: {
    btn: 'h-9 w-9 text-sm',
    icon: 17,
    badge: 'text-xs py-1 px-2.5',
  },
  lg: {
    btn: 'h-11 w-11 text-base',
    icon: 20,
    badge: 'text-xs py-1.5 px-3',
  },
};

export const VoiceDictationButton: React.FC<VoiceDictationButtonProps> = ({
  onTranscript,
  className = '',
  size = 'sm',
  title = 'Dictar con voz',
  disabled = false,
  showLabel = false,
  label = 'Dictar',
}) => {
  const { isListening, isSupported, toggleListening } = useSpeechRecognition({
    onTranscript: (text) => {
      onTranscript(text);
    },
  });

  const sizeConfig = SIZE_STYLES[size] || SIZE_STYLES.sm;

  // If the browser doesn't support Web Speech API, don't show the button or keep it disabled
  if (!isSupported) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    toggleListening();
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        title={isListening ? 'Detener dictado' : title}
        aria-label={isListening ? 'Detener dictado' : title}
        className={`relative inline-flex items-center justify-center font-medium transition-all duration-200 select-none ${
          showLabel ? 'px-3 py-1.5 rounded-xl gap-1.5 text-xs' : `${sizeConfig.btn} rounded-full`
        } ${
          isListening
            ? 'bg-rose-500 text-white shadow-md shadow-rose-500/30 ring-4 ring-rose-400/40 animate-pulse'
            : 'text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${className}`}
      >
        {isListening ? (
          <>
            <Mic className="animate-bounce" size={sizeConfig.icon} />
            {showLabel && <span className="font-semibold text-white">Escuchando...</span>}
          </>
        ) : (
          <>
            <Mic size={sizeConfig.icon} />
            {showLabel && <span>{label}</span>}
          </>
        )}

        {/* Pulse ring for active recording */}
        {isListening && (
          <span className="absolute -inset-1 rounded-full animate-ping bg-rose-400 opacity-25 pointer-events-none" />
        )}
      </button>

      {/* Floating badge feedback when listening */}
      {isListening && !showLabel && (
        <span
          className={`absolute bottom-full mb-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 text-white font-medium shadow-lg pointer-events-none z-50 animate-in fade-in zoom-in-95 ${sizeConfig.badge}`}
        >
          🎙️ Escuchando...
        </span>
      )}
    </div>
  );
};

export default VoiceDictationButton;
